import type { TFunction } from 'i18next';
import { parseFeedTimestamp } from './feedModel';

/**
 * Relative time for feeds and activity streams.
 *
 * Every string goes through `t` - the age of a post is one of the most-read
 * bits of text in the app and it cannot be English-only. Interpolation uses
 * `{{n}}` rather than `{{count}}` on purpose: `count` puts i18next into
 * plural-key resolution, and these are numeric abbreviations ("3d ago") that
 * do not pluralize.
 */

export interface FeedTimeLabel {
  /** Short relative age - "3d ago". */
  label: string;
  /** Full local date + time, for the element's `title`. */
  title: string;
}

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/** The absolute, locale-formatted timestamp shown on hover. */
export const formatAbsoluteTime = (timestamp?: string): string => {
  if (!timestamp) return '';
  const date = parseFeedTimestamp(timestamp);
  if (isNaN(date.getTime())) return timestamp;
  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export const formatRelativeTime = (
  timestamp: string | undefined,
  t: TFunction,
  now: Date = new Date()
): string => {
  if (!timestamp) return '';
  const date = parseFeedTimestamp(timestamp);
  if (isNaN(date.getTime())) return timestamp;

  const diff = now.getTime() - date.getTime();

  // A clock skew of a few seconds must not render as a post from the future.
  if (diff < MINUTE) return t('Just now');
  if (diff < HOUR) return t('{{n}}m ago', { n: Math.floor(diff / MINUTE) });
  if (diff < DAY) return t('{{n}}h ago', { n: Math.floor(diff / HOUR) });
  if (diff < 7 * DAY) return t('{{n}}d ago', { n: Math.floor(diff / DAY) });

  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
  });
};

/** Both halves at once - what a timestamp element needs. */
export const feedTimeLabel = (
  timestamp: string | undefined,
  t: TFunction,
  now: Date = new Date()
): FeedTimeLabel => ({
  label: formatRelativeTime(timestamp, t, now),
  title: formatAbsoluteTime(timestamp),
});
